import React, { useEffect, useState } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import { useSelector } from 'react-redux';
import {
  getSensorsInUseInPlannings,
  getTasksFromViewPlanning,
} from 'services/api';

interface SensorItem {
  sensor_id: number;
  sensor_name: string;
  view_type: string;
}

interface SensorSelectorProps {
  sensorList: SensorItem[];
  selectedSensors: any;
  setSelectedSensors: (selected: any[]) => void;
  idView?: number;
}

const SensorSelector = (props: SensorSelectorProps) => {
  const viewList = useSelector((state: any) => state.vue.views_list);
  const { sensorList, selectedSensors, setSelectedSensors } = props;
  const [availableSensors, setAvailableSensors] =
    React.useState<SensorItem[]>(sensorList);
  const [alreadyUsedSensors, setAlreadyUsedSensors] = React.useState<string[]>(
    []
  );
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    const checkSensorsAlreadyInUse = async (projectId: number) => {
      const responseGetAlreadyUsedSensors = await getSensorsInUseInPlannings({
        projectId,
      });
      const potentiallySensorInCurrentView: SensorItem[] = [];
      if (props.idView) {
        const responseGetTasksOfCurrentView = await getTasksFromViewPlanning({
          viewId: props.idView,
        });
        const usedInCurrentViewSensors =
          responseGetTasksOfCurrentView.data.tasks[0].sensors_list.split(',');

        sensorList.map((sensor: SensorItem) => {
          if (usedInCurrentViewSensors.includes('' + sensor.sensor_id)) {
            potentiallySensorInCurrentView.push(sensor);
          }
        });
        setSelectedSensors(
          selectedSensors.concat(potentiallySensorInCurrentView)
        );
      }
      const alreadyUsedSensors =
        responseGetAlreadyUsedSensors.data.sensors_ids?.filter(
          (sensorId: string) =>
            !potentiallySensorInCurrentView.some(
              (sensor: SensorItem) => sensor.sensor_id === parseInt(sensorId)
            )
        );
      setAlreadyUsedSensors(alreadyUsedSensors ? alreadyUsedSensors : []);
      setAvailableSensors(sensorList);
    };
    checkSensorsAlreadyInUse(viewList[0].project_id);
  }, [sensorList]);

  const tableConfig = {
    columns: [
      { dataField: 'sensor_id', text: 'ID du capteur', sort: true },
      {
        dataField: 'sensor_name',
        text: 'Nom du capteur',
        sort: true,
        formatter: (cell: string, row: SensorItem) => {
          const isUsed = alreadyUsedSensors?.includes('' + row.sensor_id);
          return (
            <span
              style={{
                textDecoration: isUsed ? 'line-through' : 'none',
                color: isUsed ? '#999999' : 'inherit',
              }}
            >
              {cell}
            </span>
          );
        },
      },
    ],
    selectRow: {
      mode: 'checkbox',
      clickToSelect: true,
      // Capteurs non sélectionnables (déjà utilisés)
      nonSelectable: alreadyUsedSensors.map((id) => parseInt(id)),
      nonSelectableStyle: {
        backgroundColor: '#f5f5f5',
        color: '#999999',
        opacity: 0.7,
      },
      style: {
        background: '#a6153e',
        color: 'white',
      },
      selected: selectedSensors.map((sensor: SensorItem) => {
        return sensor.sensor_id;
      }),
      onSelect: (row: any, isSelect: boolean) => {
        if (isSelect) {
          setSelectedSensors([...selectedSensors, row]);
        } else {
          setSelectedSensors(
            selectedSensors.filter(
              (sensor: SensorItem) => sensor.sensor_id !== row.sensor_id
            )
          );
        }
      },
      onSelectAll: (isSelect: boolean, rows: SensorItem[]) => {
        // Filtrer les capteurs non sélectionnables pour la sélection globale
        const selectableRows = rows.filter(
          (row) => !alreadyUsedSensors.includes('' + row.sensor_id)
        );
        if (isSelect) {
          setSelectedSensors(selectableRows);
        } else {
          setSelectedSensors([]);
        }
      },
    },
  };
  return (
    <div>
      {/* En-tête avec bouton d'information */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          padding: '10px',
          backgroundColor: '#f8f9fa',
          borderRadius: '5px',
          border: '1px solid #dee2e6',
        }}
      >
        <h6 style={{ margin: 0, color: '#495057' }}>Sélection des capteurs</h6>
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            style={{
              backgroundColor: 'rgb(60, 57, 53)',
              border: 'none',
              borderRadius: '50%',
              width: '30px',
              height: '30px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold',
            }}
            onMouseEnter={() => setShowInfo(true)}
            onMouseLeave={() => setShowInfo(false)}
            onClick={() => setShowInfo(!showInfo)}
          >
            i
          </button>
          {showInfo && (
            <div
              style={{
                position: 'absolute',
                top: '35px',
                right: '0',
                backgroundColor: '#343a40',
                color: 'white',
                padding: '12px',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                zIndex: 1000,
                width: '300px',
                fontSize: '13px',
                lineHeight: '1.4',
              }}
            >
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                ℹ️ Information sur les capteurs
              </div>
              <div style={{ marginBottom: '6px' }}>
                • <strong>Capteurs disponibles :</strong> Peuvent être
                sélectionnés
              </div>
              <div style={{ marginBottom: '6px' }}>
                • <strong>Capteurs grisés :</strong> Déjà utilisés dans d'autres
                planifications
              </div>
              <div style={{ color: '#ffc107' }}>
                💡 Pour utiliser un capteur grisé, désélectionnez-le d'abord
                dans le projet où il est actuellement utilisé.
              </div>
              {/* Petite flèche pointant vers le bouton */}
              <div
                style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '10px',
                  width: '0',
                  height: '0',
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderBottom: '6px solid #343a40',
                }}
              ></div>
            </div>
          )}
        </div>
      </div>

      {/* Tableau des capteurs */}
      <div
        style={{
          maxHeight: '400px',
          overflowY: 'auto',
          border: '1px solid #dee2e6',
          borderRadius: '5px',
        }}
      >
        <BootstrapTable
          keyField={'sensor_id'}
          data={availableSensors}
          columns={tableConfig.columns}
          selectRow={tableConfig.selectRow}
        />
      </div>
    </div>
  );
};

export default SensorSelector;
